/**
 * Notification Handlers & Routers
 * Centralizes navigation logic for all notification types
 * Avoids hardcoding routes in UI components
 */

import { NotificationKey } from "../types/notification";

export interface NotificationHandler {
    /**
     * Destination route when user clicks notification
     * Use dynamic segments like :id if needed
     */
    route: (metadata?: Record<string, any>) => string;

    /**
     * Action to perform when notification is clicked
     * Usually navigation, but can be extended for other actions
     */
    action?: (metadata?: Record<string, any>) => void;

    /**
     * Optional: Close panel after clicking?
     */
    closePanel?: boolean;
}

/**
 * Notification handlers registry
 * Maps each notification type to its handling logic
 */
export const notificationHandlers: Record<NotificationKey, NotificationHandler> =
{
    // ============ Order Notifications ============
    order_placed: {
        route: () => "/admin/orders",
        closePanel: true,
    },

    order_cancelled: {
        route: () => "/admin/orders",
        closePanel: true,
    },

    order_delivered: {
        route: () => "/admin/orders",
        closePanel: true,
    },

    // ============ Payment Notifications ============
    payment_received: {
        route: () => "/admin/payments",
        closePanel: true,
    },

    // ============ Store Notifications ============
    store_disabled: {
        route: () => "/admin/stores",
        closePanel: true,
    },

    store_enabled: {
        route: () => "/admin/stores",
        closePanel: true,
    },

    store_suggested: {
        route: () => "/admin/store-suggestions",
        closePanel: true,
    },

    // ============ Product Notifications ============
    product_approved: {
        route: () => "/admin/products",
        closePanel: true,
    },

    product_rejected: {
        route: () => "/admin/products",
        closePanel: true,
    },

    // ============ Review Notifications ============
    review_added: {
        route: () => "/admin/reviews",
        closePanel: true,
    },

    // ============ Feedback Notifications ============
    customer_feedback: {
        route: () => "/admin/feedback",
        closePanel: true,
    },

    // ============ Offer Notifications ============
    offer_created: {
        route: () => "/admin/offers",
        closePanel: true,
    },

    // ============ User/Owner Notifications ============
    owner_registered: {
        route: () => "/admin/owners",
        closePanel: true,
    },

    merchant_registered: {
        route: () => "/admin/merchant-registrations",
        closePanel: true,
    },

    // ============ System Notifications ============
    system_alert: {
        route: () => "/admin",
        closePanel: false,
    },

    admin_action: {
        route: () => "/admin",
        closePanel: false,
    },

    promotion_ended: {
        route: () => "/admin/promotions",
        closePanel: true,
    },
};

/**
 * Get the handler for a notification type
 * Falls back to a default handler if not found
 */
export const getNotificationHandler = (
    notificationKey: NotificationKey
): NotificationHandler => {
    return (
        notificationHandlers[notificationKey] || {
            route: () => "/admin",
            closePanel: true,
        }
    );
};

/**
 * Get the destination route for a notification
 */
export const getNotificationRoute = (
    notificationKey: NotificationKey,
    metadata?: Record<string, any>
): string => {
    const handler = getNotificationHandler(notificationKey);
    return handler.route(metadata);
};

/**
 * Check if panel should close after clicking notification
 */
export const shouldClosePanelAfterNotification = (
    notificationKey: NotificationKey
): boolean => {
    const handler = getNotificationHandler(notificationKey);
    return handler.closePanel ?? true;
};
